import React from 'react';

import { pathToRegexp } from 'path-to-regexp';

import {
  FormFields,
  LanguageForm,
} from '../../components/LanguageForm/LanguageForm';
import { nextRoutes } from '../../constants/nextRouting';
import { entityPathHooks } from '../../hooks/entityPathHooks/entityPathHooks';
import { messageHooks } from '../../hooks/messageHooks/messageHooks';
import { routerHooks } from '../../hooks/routerHooks/routerHooks';
import { storeSwitcherHooks } from '../../hooks/storeSwitcherHooks/storeSwitcherHooks';
import {
  AppDataStoreData,
  GlobalMessages,
  StoreSwitcherMessages,
} from '../../types/Messages';
import { CountryCode } from '../../types/apiEnums';
import { CountryCombination } from '../../types/apiTypes';
import { ClientLanguageLink } from '../../types/common';
import { languageUtils } from '../../utils/languageUtils/languageUtils';
import { localizeUtils } from '../../utils/localize/localizeUtils';

const nonEntityPages = [
  nextRoutes.basketPageRoute.id,
  nextRoutes.checkoutPageRoute.id,
  nextRoutes.confirmationPageRoute.id,
  nextRoutes.doublerHomePageRoute.id,
  nextRoutes.doublerConfirmationPageRoute.id,
  nextRoutes.helpdeskRoute.id,
  nextRoutes.cookiesPageRoute.id,
  nextRoutes.accountIdentifyPageRoute.id,
  nextRoutes.loginPageRoute.id,
  nextRoutes.accountRegisterPageRoute.id,
  nextRoutes.accountRegisterSuccessPageRoute.id,
  nextRoutes.forgotPasswordPageRoute.id,
  nextRoutes.forgotPasswordConfirmPageRoute.id,
  nextRoutes.newPasswordPageRoute.id,
  nextRoutes.newPasswordConfirmationPageRoute.id,
  nextRoutes.accountOrdersPageRoute.id,
  nextRoutes.accountAddressBookPageRoute.id,
  nextRoutes.accountDataPageRoute.id,
  nextRoutes.notFoundPageRoute.id,
];

export type UseGetHandleLanguageSelectedParams = {
  countriesCombinations: CountryCombination[] | null;
  languageLinks: ClientLanguageLink[] | null;
  onClose: () => void;
  prevStateLanguage: string;
  setLanguage: (value: string) => void;
};

export type UseRedirectWithLanguagePathCallbackParams = {
  country: string | null;
  isCountryChanged: boolean;
  language: string;
  languageLinks: ClientLanguageLink[] | null;
  prevCountry: string | null;
  prevLanguage: string;
};

export type UseGetPageMatchingCallbackParams = {
  country: string;
  pathForNonEntityPages: string;
  prevLanguage: string;
};

export const hooks = {
  useGetStates() {
    const storeData = messageHooks.useSubjectState(AppDataStoreData.StoreData);
    const language = messageHooks.useSubjectState(GlobalMessages.Language);
    const setLanguage = messageHooks.useNext(GlobalMessages.Language);
    const languageLinks = messageHooks.useSubjectState(StoreSwitcherMessages.LanguageLinks);
    const countriesCombinations = storeData?.countriesCombinations || null;
    const selectedCountry = messageHooks.useSubjectState(StoreSwitcherMessages.SelectedCountry);
    const isNonBlockPart = messageHooks.useSubjectState(StoreSwitcherMessages.isNonBlockPart);
    return {
      countriesCombinations,
      language,
      languageLinks,
      selectedCountry,
      setLanguage,
      isNonBlockPart,
    };
  },

  useGetPageMatchingCallback() {
    return React.useCallback(
      ({ country, prevLanguage, pathForNonEntityPages }: UseGetPageMatchingCallbackParams) => {
        return nonEntityPages.find(el => {
          const regExp = pathToRegexp(
            localizeUtils.getMessageByCountryAndLocale(country, prevLanguage, el),
            [],
            {
              sensitive: true,
            },
          );
          if (regExp.exec(pathForNonEntityPages)) return el;
          return undefined;
        });
      },
      [],
    );
  },

  useRedirectWithLanguagePathCallback() {
    const search = routerHooks.useSearch();
    const pathForNonEntityPages = entityPathHooks.useEntityPathname();
    const getPageMatching = hooks.useGetPageMatchingCallback();
    return React.useCallback(
      ({
        language,
        languageLinks,
        country,
        prevLanguage,
        isCountryChanged,
        prevCountry,
      }: UseRedirectWithLanguagePathCallbackParams) => {
        const isDefaultLanguage = language === languageUtils.getDefaultLanguage();
        const homeRedirect = isDefaultLanguage ? `/${search}` : `/${language}${search}`;

        const langElem = languageLinks?.find(
          el => el.countryCode.toLowerCase() === country && el.langCode === language,
        );

        if (!country || !prevCountry) {
          return homeRedirect;
        }

        const pageMatches = getPageMatching({
          country: prevCountry,
          prevLanguage,
          pathForNonEntityPages,
        });

        if (pageMatches) {
          if (isCountryChanged) {
            return homeRedirect;
          }
          const newStaticRoute = localizeUtils.getMessageByCountryAndLocale(
            country,
            language,
            pageMatches,
          );
          return isDefaultLanguage
            ? `${newStaticRoute}${search}`
            : `/${language}${newStaticRoute}${search}`;
        }

        if (!langElem) {
          return homeRedirect;
        }

        return `${langElem.url}${search}`;
      },
      [getPageMatching, pathForNonEntityPages, search],
    );
  },

  useGetHandleLanguageSelected({
    onClose,
    languageLinks,
    prevStateLanguage,
    setLanguage,
  }: UseGetHandleLanguageSelectedParams) {
    const getRedirectPath = hooks.useRedirectWithLanguagePathCallback();
    return React.useCallback(
      (formFields: FormFields) => {
        const language = formFields.language.toLowerCase();
        const lang = language === languageUtils.getDefaultLanguage() ? '' : language;
        const prevLanguage =
          prevStateLanguage === '' ? languageUtils.getDefaultLanguage() : prevStateLanguage;
        const isCountryChanged = CONFIG.cadeaubonGraphql.country !== formFields.country;

        const redirectPath = getRedirectPath({
          language,
          languageLinks,
          country: String(formFields.country).toLowerCase(),
          prevLanguage,
          isCountryChanged,
          prevCountry: CONFIG.cadeaubonGraphql.country.toLowerCase(),
        });

        setLanguage(lang);
        onClose();
        window.location.href = redirectPath;
      },
      [
        getRedirectPath,
        languageLinks,
        onClose,
        prevStateLanguage,
        setLanguage,
      ],
    );
  },
};

export type Props = {
  onClose: () => void;
};

export const LanguageFormContainer = (props: Props) => {
  const { onClose } = props;

  const {
    countriesCombinations,
    language,
    languageLinks,
    setLanguage,
    isNonBlockPart,
  } = hooks.useGetStates();

  const initialCountryCode = storeSwitcherHooks.useGetInitialCountryCode(isNonBlockPart);

  const onLanguageSelected = hooks.useGetHandleLanguageSelected({
    countriesCombinations,
    onClose,
    languageLinks,
    prevStateLanguage: language,
    setLanguage,
  });

  const initialCountry = initialCountryCode ?? CONFIG.cadeaubonGraphql.country;

  return (
    <LanguageForm
      onClose={onClose}
      countriesCombinations={countriesCombinations}
      onLanguageSelected={onLanguageSelected}
      language={language}
      initialCountry={initialCountry as CountryCode}
    />
  );
};

LanguageFormContainer.displayName = 'LanguageFormContainer';
